import { useState } from "react";
import { useNavigate, useRevalidator } from "react-router-dom";
import toast from "react-hot-toast";
import { PiArrowRightBold, PiCaretDownBold, PiCheckBold, PiProhibitDuotone, PiXCircleDuotone } from "react-icons/pi";
import { Dropdown, Text } from "rizzui";
import { cn, getStatusBadge } from "@steelyard/theme";
import {
  advanceOrder,
  approveOrder,
  beforePacked,
  nextAdvanceTargets,
  overrideOrderState,
  overrideTargets,
  reached,
  stateLabel,
  stepsBefore,
  type OrderFacts,
  type OrderSummary,
} from "../../lib/services/ordering";
import { problemMessage } from "../dashboard/tone";
import { useConfirm } from "../confirm-modal";
import { completeStepsPath, orderPath } from "../../lib/paths";
import { canCompleteFrom } from "../../lib/complete-order";

/** What makes an order reach each state `stepsBefore` can name. */
const STEP_HOW: Record<string, string> = {
  approved: "an administrator approves it, or its payment is confirmed if prepaid.",
  picking_packing: "assign it for packing from the order page; it moves on when packing starts.",
  packed: "the warehouse completes the pack in its app, recording the packed quantities.",
  invoiced: "Billing raises the invoice from the packed quantities, and it is finalised.",
  dispatched: "it is dispatched from the Dispatch screen.",
  delivered: "the delivery is recorded on the Dispatch screen.",
};

/**
 * In place of the force confirm, when the override endpoint would refuse the move (`stepsBefore`):
 * every step that has to happen first, in order. With `complete`, the Super Admin may instead take
 * them all now (`pages/orders/Complete.tsx`); without it, the notice only closes. Shared with the
 * order detail page.
 */
export function stepsFirst(orderNumber: string, target: string, steps: string[], facts: OrderFacts, complete?: () => void) {
  return {
    title: `${orderNumber} cannot be set to ${stateLabel(target)} yet`,
    consequence: (
      <>
        {!facts.packRecorded
          ? "Its packing has not been completed, so nothing that follows it has happened either."
          : !facts.invoiced
            ? "It has no finalised invoice yet, so it cannot leave the warehouse."
            : "Each of these is a step with its own record, so it is taken rather than forced."}{" "}
        These come first, in order:
        <ol className="mt-2 list-decimal space-y-1 ps-5">
          {steps.map((step) => (
            <li key={step}>
              <strong>{stateLabel(step)}</strong> — {STEP_HOW[step]}
            </li>
          ))}
        </ol>
        {complete ? (
          <p className="mt-3">
            You can complete them all now instead, entering what each step needs.
          </p>
        ) : null}
      </>
    ),
    ...(complete ? { confirmLabel: "Complete these steps now…", onConfirm: complete } : {}),
  };
}

/**
 * An order's state, changeable from its list row. COPIED from the status-select-in-a-cell of
 * vendor/isomorphic/apps/isomorphic/src/app/shared/appointment/appointment-list/list/columns.tsx
 * (the row's own status is the control), built as the rizzui `Dropdown` of
 * vendor/isomorphic/apps/isomorphic/src/app/shared/support/inbox/action-dropdown.tsx, because what
 * it offers are MOVES, not a free choice of state.
 *
 * It offers exactly what the order detail page's Decisions card offers, under the same conditions —
 * Approve (`awaiting_approval`, `orders.administer`), Move to the ONE next step `nextAdvanceTargets` allows (not cancelled,
 * `orders.advance`), Reject… and Cancel… (`orders.write`) — and nothing it doesn't: "invoiced" is not
 * offered, only Billing makes that move. Whether a move is legal from the current state is the API's
 * call, not this menu's; a refusal comes back as the server's own words in a toast. Reject… and
 * Cancel… need a reason, so they open the detail page's own `?act=` ReasonDialog rather than
 * committing from here.
 */
export function OrderStateMenu({
  order,
  mayWrite,
  mayApprove,
  mayOverride = false,
  mayAdvance = false,
}: {
  order: OrderSummary;
  mayWrite: boolean;
  mayApprove: boolean;
  mayOverride?: boolean;
  mayAdvance?: boolean;
}) {
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();

  const awaiting = order.state === "awaiting_approval";
  const open = order.state !== "cancelled";
  const canApprove = awaiting && mayApprove;
  // Advancing is `orders.advance` (the endpoint's own policy), not `orders.administer` — as on Detail.
  const moves = open && mayAdvance ? nextAdvanceTargets(order.state) : [];
  const canReject = awaiting && mayWrite;
  // OrderStateMachine: invoiced, dispatched, delivered and cancelled have no edge to cancelled.
  const canCancel = !["invoiced", "dispatched", "delivered", "cancelled"].includes(order.state) && mayWrite;
  // Super Admin: every other state, outside the lifecycle table (the normal next step stays above).
  const forced = mayOverride ? overrideTargets(order.state).filter((s) => !(moves as readonly string[]).includes(s)) : [];

  if (!canApprove && moves.length === 0 && !canReject && !canCancel && forced.length === 0) return getStatusBadge(order.state);

  async function apply(change: () => Promise<unknown>, done: string) {
    setBusy(true);
    try {
      await change();
      toast.success(`${order.orderNumber}: ${done}`);
      revalidator.revalidate();
    } catch (err) {
      toast.error(problemMessage(err, `${order.orderNumber}: Infra Junction refused that change`));
    } finally {
      setBusy(false);
    }
  }

  // The list row carries no lines or invoice, so each counts as there once the state is past it;
  // the API refuses the rest.
  const force = (target: string) => {
    const facts = { packRecorded: !beforePacked(order.state), invoiced: reached(order.state, "invoiced") };
    const steps = stepsBefore(order.state, facts, target);
    const complete = canCompleteFrom(order.state) ? () => navigate(completeStepsPath(order.orderNumber, target)) : undefined;
    if (steps.length > 0) return confirm(stepsFirst(order.orderNumber, target, steps, facts, complete));
    confirm({
      title: `Force ${order.orderNumber} to ${stateLabel(target)}?`,
      consequence:
        "This skips the normal order flow. Only the state changes — stock, packing and invoices are not touched.",
      confirmLabel: `Set to ${stateLabel(target)}`,
      reason: "Reason",
      onConfirm: (reason) => apply(() => overrideOrderState(order.id, target, reason), `set to ${stateLabel(target)}`),
    });
  };

  return (
    <Dropdown placement="bottom-start">
      <Dropdown.Trigger
        disabled={busy}
        aria-label={`Change the state of ${order.orderNumber} (now ${stateLabel(order.state)})`}
        className={cn(
          "group inline-flex items-center gap-1.5 rounded-md border border-transparent px-1.5 py-1 -ms-1.5 transition-colors hover:border-muted hover:bg-gray-50",
          busy && "animate-pulse",
        )}
      >
        {getStatusBadge(order.state)}
        <PiCaretDownBold aria-hidden className="size-3 text-gray-400 group-hover:text-gray-700" />
      </Dropdown.Trigger>
      <Dropdown.Menu className="max-h-96 w-60 overflow-y-auto">
        {canApprove ? (
          <Dropdown.Item onClick={() => apply(() => approveOrder(order.id), "approved")} className="gap-2.5 text-sm text-green-dark">
            <PiCheckBold aria-hidden className="size-4 shrink-0" />
            Approve
          </Dropdown.Item>
        ) : null}
        {moves.length > 0 ? (
          <Text className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Move to</Text>
        ) : null}
        {moves.map((target) => (
          <Dropdown.Item key={target} onClick={() => apply(() => advanceOrder(order.id, target), `moved to ${stateLabel(target)}`)} className="gap-2.5 text-sm">
            <PiArrowRightBold aria-hidden className="size-4 shrink-0 text-gray-500" />
            {stateLabel(target)}
          </Dropdown.Item>
        ))}
        {forced.length > 0 ? (
          <>
            <div role="separator" className="my-1 border-t border-muted" />
            <Text className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Any state (Super Admin)</Text>
            {forced.map((target) => (
              <Dropdown.Item key={target} onClick={() => force(target)} className="gap-2.5 text-sm">
                <PiArrowRightBold aria-hidden className="size-4 shrink-0 text-orange-dark" />
                {stateLabel(target)}
              </Dropdown.Item>
            ))}
          </>
        ) : null}
        {canReject || canCancel ? <div role="separator" className="my-1 border-t border-muted" /> : null}
        {canReject ? (
          <Dropdown.Item onClick={() => navigate(`${orderPath(order.orderNumber)}?act=reject`)} className="gap-2.5 text-sm text-red-dark">
            <PiProhibitDuotone aria-hidden className="size-4 shrink-0" />
            Reject…
          </Dropdown.Item>
        ) : null}
        {canCancel ? (
          <Dropdown.Item onClick={() => navigate(`${orderPath(order.orderNumber)}?act=cancel`)} className="gap-2.5 text-sm text-red-dark">
            <PiXCircleDuotone aria-hidden className="size-4 shrink-0" />
            Cancel order…
          </Dropdown.Item>
        ) : null}
      </Dropdown.Menu>
    </Dropdown>
  );
}
